import React from 'react'
import { AnchorsOfForms } from '@constants/forms'
import { Section } from '@shared/components/section'
import { Button } from '@shared/componentsStyled/Button'
import { changeDocumentHash } from '@shared/pipes/dom'
import scrollToElement from '@shared/pipes/scroll'
import styled, { CSSObject } from 'styled-components'

import { Input } from '../Input'

import { CODE_MAX_LENGTH, NAME_MAX_LENGTH, useSectionActivate } from './useSectionActivate'

export const goToActivateLink = () => {
	scrollToElement(`#${AnchorsOfForms.activate}`, { time: 0 }, () => {
		changeDocumentHash(AnchorsOfForms.activate)
	})
}

export function SectionActivate(): JSX.Element {
	const { isSubmitted, formik, inputDisabled, error } = useSectionActivate()

	return (
		<Section id={AnchorsOfForms.activate}>
			<ViewRoot>
				<TextTitle>Активировать сертификат</TextTitle>
				<TextSubTitle>
					Введите код из вашего сертификата, чтобы подарок начал зарабатывать.
				</TextSubTitle>
				<form onSubmit={formik.handleSubmit}>
					<ViewRow>
						<ViewForm>
							<Label htmlFor='investFondConsultName'>Код из сертификата</Label>
							<Input
								id='investFondConsultName'
								name='code'
								type='text'
								placeholder='Код'
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.code}
								hasError={!!formik.errors.code}
								maxLength={CODE_MAX_LENGTH}
								disabled={inputDisabled}
							/>
							<ViewInputInfo>
								{!!formik.errors.code && <TextError>{formik.errors.code}</TextError>}
							</ViewInputInfo>
							<Label htmlFor='investFondConsultName'>Как к вам обращаться</Label>
							<Input
								id='investFondConsultName'
								name='name'
								type='text'
								placeholder='Имя'
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								value={formik.values.name}
								hasError={!!formik.errors.name}
								maxLength={NAME_MAX_LENGTH}
								disabled={inputDisabled}
							/>
							<ViewInputInfo>
								{!!formik.errors.name && <TextError>{formik.errors.name}</TextError>}
							</ViewInputInfo>
							<Label htmlFor='investFondConsultPhone'>Ваш номер телефона</Label>
							<Input
								mask='+7 999 999-99-99'
								id='investFondConsultPhone'
								name='phone'
								value={formik.values.phone}
								type='text'
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								placeholder='Телефон'
								hasError={!!formik.errors.phone}
								disabled={inputDisabled}
							/>
							<ViewInputInfo last>
								{!!formik.errors.phone && <TextError>{formik.errors.phone}</TextError>}
							</ViewInputInfo>
							<ViewFormInfo>{!!error && <TextError>{error}</TextError>}</ViewFormInfo>
						</ViewForm>
						<ViewRes>
							<ViewResRow>
								<TextLabelSmall>Как оплатить и получить сертификат?</TextLabelSmall>
							</ViewResRow>
							<ViewResList>
								<ViewResItem>
									<Number>1</Number>
									<div>
										Заполните форму с кодом активации не позднее 30 дней с момента покупки
										сертификата
									</div>
								</ViewResItem>
								<ViewResItem>
									<Number>2</Number>
									<div>
										С вами свяжется наш менеджер, чтобы сопроводить вас по процессу открытия
										«Сбережения»
									</div>
								</ViewResItem>
								<ViewResItem>
									<Number>3</Number>
									<div>
										Установите приложение и подпишите документы на открытие «Сбережения» онлайн
									</div>
								</ViewResItem>
							</ViewResList>
						</ViewRes>
					</ViewRow>
					<ViewAction>
						<ButtonStyled type='submit' success={isSubmitted} disabled={inputDisabled}>
							{isSubmitted ? 'Отправлено' : 'Активировать'}
						</ButtonStyled>
						<Description>
							Нажимая кнопку «Активировать» получатель подарочного сертификата выражает согласие на
							правила получения подарка в соответствии с опубликованным{' '}
							<a
								className='main-link'
								href='https://storage.yandexcloud.net/creditclub-frontend/static/docs/creditclub/bonus/gift_certificate.pdf '
								target='_blank'
								rel='noopener noreferrer nofollow'
							>
								Публичным договором-офертой «Подарочный сертификат на сбережения»
							</a>{' '}
							и выражает{' '}
							<a
								className='main-link'
								href='https://credit.club/agreement'
								target='_blank'
								rel='noopener noreferrer nofollow'
							>
								согласие с обработкой персональных данных
							</a>
						</Description>
					</ViewAction>
				</form>
			</ViewRoot>
		</Section>
	)
}

const ViewRoot = styled.div(({ theme }) => ({
	border: '1px solid rgba(216, 234, 228, 0.25)',
	borderRadius: 40,
	boxShadow: '0px 10px 30px rgba(163, 198, 186, 0.25)',
	marginBottom: 15,
	padding: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		marginBottom: 40,
		padding: '60px 40px',
	},
}))

const ViewRes = styled.div(
	({ theme }) =>
		({
			flex: 1,
			width: '100%',
			backgroundColor: theme.colors.lightmint,
			borderRadius: 8,
			height: 'min-content',
			padding: '10px 15px',
			display: 'flex',
			flexDirection: 'column',
			marginTop: 32,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				padding: '20px 30px',
			},
		} as CSSObject)
)

const ViewResRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			width: '100%',
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const TextLabelSmall = styled.label(({ theme }) => ({
	...theme.typography.text14_18,
	opacity: 0.5,
	display: 'block',
	marginBottom: 5,
	whiteSpace: 'normal',

	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		whiteSpace: 'nowrap',
	},
}))

const ViewResList = styled.div(
	() =>
		({
			display: 'flex',
			flexDirection: 'column',
		} as CSSObject)
)

const Number = styled.div(
	({ theme }) =>
		({
			...theme.typography.text18_24,
			textAlign: 'center',
			display: 'block',
			color: 'white',
			backgroundColor: 'black',
			borderRadius: '50%',
			width: 24,
			height: 24,
			marginRight: 8,
		} as CSSObject)
)

const ViewResItem = styled.div(({ theme }) => ({
	...theme.typography.text18_24,
	display: 'grid',
	gridTemplateColumns: 'auto auto',
	':not(:last-child)': {
		marginBottom: 8,
	},
}))

const TextTitle = styled.h2(({ theme }) => ({
	...theme.typography.header30_36,
	marginBottom: 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.header56_60,
		marginBottom: 15,
	},
}))

const TextSubTitle = styled.p(({ theme }) => ({
	...theme.typography.text18_24,
	marginBottom: 20,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text24_32,
		marginBottom: 30,
	},
}))

const ViewRow = styled.div(
	({ theme }) =>
		({
			display: 'flex',
			flexDirection: 'column',
			marginBottom: 30,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				flexDirection: 'row',
			},
		} as CSSObject)
)

const ViewForm = styled.div(
	({ theme }) =>
		({
			position: 'relative',
			flex: 1,
			marginRight: 0,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				marginRight: 80,
			},
		} as CSSObject)
)

const Label = styled.label(({ theme }) => ({
	opacity: 0.5,
	display: 'block',
	...theme.typography.text14_18,
	marginBottom: 5,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text18_24,
		marginBottom: 10,
	},
}))

interface ViewInputInfoProps {
	last?: boolean | undefined
}
const ViewInputInfo = styled.div<ViewInputInfoProps>(({ theme, last }) => ({
	minHeight: last ? 20 : 10,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		minHeight: last ? 30 : 20,
	},
}))

const ViewFormInfo = styled.div(
	({ theme }) =>
		({
			position: 'absolute',
			bottom: -18,
			left: 0,
			[theme.mediaQueries.mediaMinWidthMobile]: {
				bottom: -20,
			},
		} as CSSObject)
)

const TextError = styled.p(({ theme }) => ({
	...theme.typography.text12_16,
	color: theme.colors.error,
	marginBottom: 2,
	[theme.mediaQueries.mediaMinWidthMobile]: {
		...theme.typography.text14_18,
	},
}))

const ViewAction = styled.div(({ theme }) => ({
	display: 'grid',
	gridGap: 24,
	gridTemplateColumns: '1fr',
	[theme.mediaQueries.mediaMinWidthMobile]: {
		gridTemplateColumns: 'min-content auto',
	},
}))

const Description = styled.div(({ theme }) => ({
	...theme.typography.text12_16,
	opacity: 0.5,
	margin: 'auto',
}))

interface ButtonStyledProps {
	success?: boolean | undefined
}
const ButtonStyled = styled(Button)<ButtonStyledProps>(({ success }) =>
	!success
		? {
				height: 'fit-content',
		  }
		: {
				height: 'fit-content',
				backgroundColor: '#49D4A7',
				'&:hover': {
					backgroundColor: '#49D4A7',
				},
				'&:active': {
					backgroundColor: '#49D4A7',
				},
		  }
)
